/* eslint-disable max-lines */
import React, {
    useContext, useRef, useCallback
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    FolderUp, Upload, FolderPlus, Info
} from 'lucide-react';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    KBContext
} from 'builders/builderKnowledge/knowledgeBuilderContext/KBGlobalContext';
import {
    Context
} from 'shared/context/GlobalState';
import {
    useScrollEndDetect
} from '../hooks/useScrollEndDetect';
import {
    FolderComponent, NewFolder, FolderLoadingComponent
} from './FolderComponent';
import InterfaceButton from './InterfaceButton';
import {
    fileManagerService
} from 'shared/api/services/BEX/fileManager.service';
import axios from 'axios';
import {
    UPLOAD_ORIGIN, SUPPORTED_FILE_TYPES
} from '../config/constants';
import {
    META_MAP_DIR
} from '../hooks/useFileHandler';

export default function FolderSection() {
    const {
        documentMap,
        setUploadProgress,
        setUploadDialogOpen,
        refreshList,
        selectedFolder,
        bucketId,
        documentIsLoading,
        setDocumentIsLoading,
        handleAddFolder,
        handleCheckMetadata,
        getCOSFormatDirectory
    } = useContext(KBContext);

    const {
        user,
        subscription,
        addNewNotifcation
    } = useContext(Context);

    const params = useParams();
    const FolderUploadRef = useRef(null);
    const fileInputRef = useRef(null);

    const handleFolderEndReached = () => {

        // Implement load more logic if needed
    };

    const handleScroll = useScrollEndDetect(50, 100, handleFolderEndReached);

    const handleUploadFolder = useCallback(async (event) => {
        event.preventDefault();

        const files = event.target.files;

        if (files && files.length > 0) {
            const prefix = getCOSFormatDirectory();
            const maxSize = maxSizeInMB * 1024 * 1024;
            const allFiles = Array.from(files).map((file, index) => {
                let status = 'pending';
                let error = null;

                if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
                    status = 'failed';
                    error = 'upload failed because of the type';
                } else if (file.size > maxSize) {
                    status = 'failed';
                    error = `File too large: Max size is ${maxSizeInMB}MB`;
                }

                return {
                    file,
                    relativePath: file.webkitRelativePath,
                    folderPath: file.webkitRelativePath.split('/').slice(0, -1).join('/'),
                    id: `${file.name}-${index}`,
                    status,
                    progress: 0,
                    error
                };
            });

            // Initialize progress
            setUploadProgress(allFiles.map(f => ({
                name: f.file.name,
                status: f.status,
                progress: f.progress,
                error: f.error
            })));
            setUploadDialogOpen(true);

            // Group by folder
            const fileMap = new Map();
            allFiles.forEach((item) => {
                if (!fileMap.has(item.folderPath)) {
                    fileMap.set(item.folderPath, []);
                }
                const existing = fileMap.get(item.folderPath);
                existing.push(item);
                fileMap.set(item.folderPath, existing);
            });

            for (const [relativePrefix, val] of fileMap.entries()) {
                const fileNamesArr = [];
                const metadataArr = [];

                // Only get pre-signed URLs for non-failed files
                const validFilesForFolder = val.filter(item => item.status !== 'failed');

                if (validFilesForFolder.length === 0) {

                    continue;
                }

                val.forEach((item) => {
                    if (item.status !== 'failed') {
                        fileNamesArr.push(item.file.name);
                        metadataArr.push(
                            JSON.stringify({
                                lastModifiedDate: item.file.lastModifiedDate,
                                userId: user._id,
                                organizationId: params.oragID,
                                subOrganizationId: params.subOragID,
                                origin: UPLOAD_ORIGIN
                            })
                        );
                    }
                });

                const finalPrefix = prefix + (prefix ? `/${relativePrefix}` : `${relativePrefix}`);
                const uploadFilesObj = {
                    organizationId: params.oragID,
                    bucketId: bucketId,
                    prefix: finalPrefix,
                    fileNames: fileNamesArr,
                    metadataArray: metadataArr
                };

                try {
                    const response = await fileManagerService.getPreSignedUrls(uploadFilesObj);
                    const {
                        preSignedUrls
                    } = response.data;

                    if (!preSignedUrls || preSignedUrls.length === 0) {
                        throw new Error('No pre-signed URLs returned from the server.');
                    }

                    // Update status to uploading only for those that aren't failed
                    setUploadProgress(prev => prev.map(p => {
                        const fileItem = validFilesForFolder.find(v => v.file.name === p.name);
                        return fileItem ? {
                            ...p,
                            status: 'uploading'
                        } : p;
                    }));

                    const uploadPromises = preSignedUrls.map(async ({
                        fileName, url
                    }) => {
                        const fileItem = validFilesForFolder.find((item) => item.file.name === fileName);
                        if (!fileItem) throw new Error(`File ${fileName} not found in selected files.`);

                        try {
                            await axios.put(url, fileItem.file, {
                                headers: {
                                    'Content-Type': fileItem.file.type
                                },
                                onUploadProgress: (progressEvent) => {
                                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                    setUploadProgress(prev => prev.map(p =>
                                        p.name === fileName ? {
                                            ...p,
                                            progress: percentCompleted
                                        } : p
                                    ));
                                }
                            });
                            setUploadProgress(prev => prev.map(p =>
                                p.name === fileName ? {
                                    ...p,
                                    status: 'completed',
                                    progress: 100
                                } : p
                            ));
                            return fileName;
                        } catch (error) {
                            setUploadProgress(prev => prev.map(p =>
                                p.name === fileName ? {
                                    ...p,
                                    status: 'failed',
                                    error: error.message
                                } : p
                            ));
                            throw error;
                        }
                    });

                    await Promise.allSettled(uploadPromises);

                } catch (error) {
                    console.error('Error uploading files:', error);
                    // Mark all files in this folder as failed
                    setUploadProgress(prev => prev.map(p => {
                        const fileItem = val.find(v => v.file.name === p.name);
                        return fileItem ? {
                            ...p,
                            status: 'failed',
                            error: error.message
                        } : p;
                    }));
                }
            }

            FolderUploadRef.current.value = [];
            event.target.value = [];
            refreshList();
        }
    }, [selectedFolder, setUploadProgress, setUploadDialogOpen, refreshList, getCOSFormatDirectory, user, params, bucketId]);

    const maxSizeInMB = subscription?.maxFileSizeInMB || 50;

    const handleFileUpload = async (e) => {
        const files = e.target.files;

        const fileNamesArr = [];
        const metadataArr = [];
        const validFiles = [];

        const prefix = getCOSFormatDirectory();
        const existingFiles = selectedFolder ? Array.from(selectedFolder.keys())
            .filter(key => key !== META_MAP_DIR)
            .map(key => {
                const item = selectedFolder.get(key);
                // Ensure item is a Map before calling .get()
                if (item instanceof Map) {
                    const meta = item.get(META_MAP_DIR);
                    return meta ? handleCheckMetadata(meta, 'name') : null;
                }
                return null;
            }).filter(Boolean) : [];

        const maxSize = maxSizeInMB * 1024 * 1024;
        const allowedTypes = SUPPORTED_FILE_TYPES;

        if (files.length > 0) {

            // Validate files
            for (let file of Array.from(files)) {
                // Check file type
                if (!allowedTypes.includes(file.type)) {
                    addNewNotifcation(`Unsupported file type: ${file.name}. Please check supported formats.`, 'warning');
                    continue;
                }
                // Check file size
                if (file.size > maxSize) {
                    addNewNotifcation(`File too large: ${file.name}. Max size is ${maxSizeInMB}MB.`, 'warning');
                    continue;
                }
                // Check for duplicate
                let finalFileName = file.name;
                if (existingFiles.includes(file.name)) {
                    // Auto-rename to keep both files
                    const nameParts = file.name.split('.');
                    const ext = nameParts.pop();
                    const baseName = nameParts.join('.');
                    finalFileName = `${baseName}_${Date.now()}.${ext}`;
                    // Create a new file with the new name
                    file = new File([file], finalFileName, {
                        type: file.type
                    });
                    addNewNotifcation(`File ${file.name} already exists. Renamed to ${finalFileName}`, 'info');
                }
                // Valid file
                validFiles.push({
                    file,
                    finalName: finalFileName,
                    status: 'pending',
                    progress: 0,
                    error: null
                });
                fileNamesArr.push(finalFileName);
                metadataArr.push(
                    JSON.stringify({
                        lastModified: file.lastModified,
                        userId: user._id,
                        organizationId: params.oragID,
                        subOrganizationId: params.subOragID,
                        origin: UPLOAD_ORIGIN
                    })
                );
            }

            if (validFiles.length === 0) {
                fileInputRef.current.value = '';
                return;
            }

            // Initialize progress
            setUploadProgress(validFiles.map(f => ({
                name: f.finalName,
                status: f.status,
                progress: f.progress,
                error: f.error
            })));
            setUploadDialogOpen(true);

            const uploadFilesObj = {
                organizationId: params.oragID,
                bucketId: bucketId,
                prefix: prefix || null,
                fileNames: fileNamesArr,
                metadataArray: metadataArr
            };

            try {
                // Step 1: Get pre-signed URLs
                const response = await fileManagerService.getPreSignedUrls(uploadFilesObj);
                const {
                    preSignedUrls
                } = response.data;

                if (!preSignedUrls || preSignedUrls.length === 0) {
                    throw new Error('No pre-signed URLs returned from the server.');
                }

                // Update status to uploading
                setUploadProgress(prev => prev.map(p => {
                    const fileItem = validFiles.find(v => v.finalName === p.name);
                    return fileItem ? {
                        ...p,
                        status: 'uploading'
                    } : p;
                }));

                // Step 2: Upload each file with progress tracking
                const uploadPromises = preSignedUrls.map(async ({
                    fileName, url
                }) => {
                    const fileItem = validFiles.find((item) => item.finalName === fileName);
                    if (!fileItem) throw new Error(`File ${fileName} not found in selected files.`);

                    try {
                        await axios.put(url, fileItem.file, {
                            headers: {
                                'Content-Type': fileItem.file.type
                            },
                            onUploadProgress: (progressEvent) => {
                                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                setUploadProgress(prev => prev.map(p =>
                                    p.name === fileName ? {
                                        ...p,
                                        progress: percentCompleted
                                    } : p
                                ));
                            }
                        });
                        setUploadProgress(prev => prev.map(p =>
                            p.name === fileName ? {
                                ...p,
                                status: 'completed',
                                progress: 100
                            } : p
                        ));
                        return fileName;
                    } catch (error) {
                        setUploadProgress(prev => prev.map(p =>
                            p.name === fileName ? {
                                ...p,
                                status: 'failed',
                                error: error.message
                            } : p
                        ));
                        throw error;
                    }
                });

                await Promise.allSettled(uploadPromises);

            } catch (error) {
                console.error('Error uploading files:', error);
                // Mark all files as failed
                setUploadProgress(prev => prev.map(p => {
                    const fileItem = validFiles.find(v => v.finalName === p.name);
                    return fileItem ? {
                        ...p,
                        status: 'failed',
                        error: error.message
                    } : p;
                }));
            }

            fileInputRef.current.value = '';
        }
    };

    const showLoadingComponent = () => {
        if (documentIsLoading) {
            return (
                <FolderLoadingComponent />
            );
        }
    };

    return (
        <>
            <div className="h-14 px-4 flex items-center justify-between border-b border-border bg-muted/10 shrink-0">
                <h3 className="font-semibold text-sm text-foreground">Folders</h3>
                <div className="flex items-center gap-1">
                    <InterfaceButton
                        content={<Info size={18} />}
                        tooltip={
                            <div className="space-y-3 py-1">
                                <div>
                                    <span className="font-bold text-foreground border-b border-border block mb-1.5 pb-0.5">Documents</span>
                                    <span className="text-muted-foreground leading-relaxed">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, JSON, TXT</span>
                                </div>
                                <div>
                                    <span className="font-bold text-foreground border-b border-border block mb-1.5 pb-0.5">Images</span>
                                    <span className="text-muted-foreground leading-relaxed">PNG, JPG, JPEG, BMP, GIF, WEBP, TIFF, TIF, ICO</span>
                                </div>
                                <div>
                                    <span className="font-bold text-foreground border-b border-border block mb-1.5 pb-0.5">Audio</span>
                                    <span className="text-muted-foreground leading-relaxed">FLAC, MP3, MP4, MPEG, MPGA, M4A, OGG, WAV, WEBM</span>
                                </div>
                                <div className="pt-2 mt-1 border-t border-border">
                                    <p className="flex justify-between items-center text-xs">
                                        <span className="font-semibold text-foreground">Max Size (Single):</span>
                                        <span className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{maxSizeInMB}MB</span>
                                    </p>
                                </div>
                            </div>
                        }
                    />
                    <InterfaceButton
                        onClick={() => {
                            console.info('Clicked Upload Folder');
                            FolderUploadRef.current.click();
                        }}
                        content={<FolderUp size={18} />}
                        tooltip="Upload folder"
                    />
                    <InterfaceButton
                        content={<Upload size={18} />}
                        tooltip="Upload files"
                        onClick={() => fileInputRef.current.click()}
                    />
                    <InterfaceButton
                        onClick={() => handleAddFolder()}
                        content={<FolderPlus size={18} />}
                        tooltip="Create new folder"
                    />
                </div>
            </div>
            <ScrollArea className="h-full" onScroll={handleScroll}>
                <div className="flex flex-col">
                    <ul className="flex-grow h-0">
                        {Array.from(documentMap.keys()).map((folderKey) => {
                            const folder = documentMap.get(folderKey);
                            if (!folder || !(folder instanceof Map)) return null;

                            const metaDir = folder.get(META_MAP_DIR);
                            if (!metaDir) return null;

                            const folderType = handleCheckMetadata(metaDir, 'type');
                            const folderName = handleCheckMetadata(metaDir, 'name') || folderKey;

                            const isNew = handleCheckMetadata(metaDir, 'isNew');

                            // If name is %__Unnamed__%, treat as new regardless of isNew flag (fallback)
                            const isUnnamed = folderName === '%__Unnamed__%';
                            const isHidden = handleCheckMetadata(metaDir, 'isHidden');

                            if (isHidden) return null;

                            if (folderType === 'folder') {
                                if (isNew || isUnnamed) {
                                    return (
                                        <NewFolder
                                            key={metaDir}
                                            folderObj={folder}
                                        />
                                    );
                                }
                                return (
                                    <FolderComponent
                                        withMenu
                                        opennable
                                        selectable
                                        folderObj={folder}
                                        key={metaDir}
                                        folderName={folderName}
                                    />
                                );
                            }
                            return null;
                        })}
                        {showLoadingComponent()}
                    </ul>
                </div>
            </ScrollArea>
            <input type="file" webkitdirectory="true"
                directory="true"
                multiple
                aria-label="Folder upload" onChange={handleUploadFolder} ref={FolderUploadRef} className="hidden" />
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.json,.txt,.png,.jpg,.jpeg,.bmp,.gif,.webp,.tiff,.tif,.ico,.flac,.mp3,.mp4,.mpeg,.mpga,.m4a,.ogg,.wav,.webm"
                onChange={(e) => {
                    setDocumentIsLoading(true);
                    handleFileUpload(e)
                        .then(() => {
                            refreshList();
                        });
                }}
            />
        </>
    );
}